import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { ScholarshipTable } from '../../classes/scholarship-table';
import { AppService } from '../../services/app.service';
import { SettingsService } from '../../services/settings.service';
import { SnackBarService } from '@polk_county_public_schools/ngx-pcps-dialogs/snackbar';
import { UserInfo, UserService } from '../../services/user.service';
import { SchoolYearPipe } from '../../pipes/schoolyear/schoolyear.pipe';
import { ErrorService } from '../../services/error.service';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
    selector: 'app-scholarship-list',
    templateUrl: './scholarship-list.component.html',
    imports: [
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatButtonModule,
        MatDividerModule,
        CommonModule,
        RouterModule,
        FormsModule,
        MatInputModule,
        SchoolYearPipe,
        MatTooltipModule
    ]
})
export class ScholarshipListComponent implements OnInit {
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly appSvc = inject(AppService);
    protected readonly settingSvc = inject(SettingsService);
    protected readonly snackBarSvc = inject(SnackBarService);
    protected readonly errorSvc = inject(ErrorService);
    protected readonly userSvc = inject(UserService);
    user: UserInfo;
    dataSource: MatTableDataSource<ScholarshipTable>;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    displayedColumns: string[] = ['MasterID', 'ScholarshipName', 'Active', 'Advertised', 'Action'];
    pageSize: number;
    pageIndex: number;
    schoolYear: string;
    input: string;

    ngOnInit() {
        this.user = this.userSvc.User;
        this.getTableData();
        this.pageSize = parseInt(sessionStorage.getItem('pageSizeSch')) || 10;
        this.pageIndex = parseInt(sessionStorage.getItem('pageIndexSch')) || 0;
        this.input = sessionStorage.getItem('filterSch') || '';
        this.getSettings();
    }

    getSettings() {
        this.settingSvc.SettingsSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(settings => {
            if (settings == null) return;
            this.schoolYear = settings.find(f => f.Label === 'School year (scholarship admin)').Value;
        });
    }

    getTableData() {
        this.appSvc.GetScholarshipTable().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: res => {
                this.dataSource = new MatTableDataSource(res);
                this.dataSource.paginator = this.paginator;
                this.dataSource.filter = this.input;
                let sortActive = sessionStorage.getItem('activeSch') || '';
                let sortDirection: SortDirection = sessionStorage.getItem('directionSch') as SortDirection || '';
                this.sortChange({ active: sortActive, direction: sortDirection });
            }, error: error => {
                this.errorSvc.HandleServiceError(error);
            }
        });
    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();
        sessionStorage.setItem('filterSch', filterValue.trim().toLowerCase());

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    handlePageEvent(e: PageEvent) {
        this.pageSize = e.pageSize;
        sessionStorage.setItem('pageSizeSch', e.pageSize.toString());
        this.pageIndex = e.pageIndex;
        sessionStorage.setItem('pageIndexSch', e.pageIndex.toString());
    }

    sortChange(sort: Sort) {
        if (!sort.active || sort.direction === '') {
            this.dataSource.data = this.dataSource.data.slice();
            return;
        }

        sessionStorage.setItem('activeSch', sort.active);
        sessionStorage.setItem('directionSch', sort.direction);

        this.dataSource.data = this.dataSource.data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'MasterID':
                    return compare(a.MasterID, b.MasterID, isAsc);
                case 'ScholarshipName':
                    return compare(a.ScholarshipName.toLowerCase(), b.ScholarshipName.toLowerCase(), isAsc);
                case 'Active':
                    return compareBool(a.Active, b.Active, isAsc);
                case 'Advertised':
                    return compareBool(a.Advertised, b.Advertised, isAsc);
                default:
                    return 0;
            }
        });
    }

}

function compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}

function compareBool(a: boolean, b: boolean, isAsc: boolean) {
    return (Number(a) < Number(b) ? -1 : 1) * (isAsc ? 1 : -1);
}